import { Query, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { ScenarioScope, scopeBase } from '../scope';
import { ScenarioDetail, ScenarioSummary } from '../types';

type ModelUpdate = { label?: string; isPrimary?: boolean; archived?: boolean };

// A family model's own routes live under its family, wherever it is listed.
const ownScope = (scenario: ScenarioSummary): ScenarioScope =>
  scenario.familyId != null
    ? { kind: 'family', id: scenario.familyId }
    : { kind: 'client', id: scenario.clientId ?? '' };

// Every models list, on any client or family page: one model can sit on several of them,
// and making one primary or archiving it changes the rest of its group.
const isModelList = ({ queryKey }: Query): boolean =>
  (queryKey[0] === 'clients' || queryKey[0] === 'families') &&
  queryKey[2] === 'cashflow-scenarios' &&
  (queryKey[3] === undefined || typeof queryKey[3] === 'object');

const isQueryOfModel =
  (scenarioId: string) =>
  ({ queryKey }: Query): boolean =>
    queryKey[2] === 'cashflow-scenarios' && queryKey[3] === scenarioId;

/** Changes made to a model from the models list, which may hold models of any scope. */
export function useModelActions() {
  const queryClient = useQueryClient();
  const refreshLists = () => queryClient.invalidateQueries({ predicate: isModelList });
  const { mutateAsync: updateModel } = useMutation({
    mutationFn: async ({
      scenario,
      update,
    }: {
      scenario: ScenarioSummary;
      update: ModelUpdate;
    }) => {
      await api.patch(`${scopeBase(ownScope(scenario))}/cashflow-scenarios/${scenario.id}`, update);
    },
    onSuccess: (_, { scenario }) =>
      Promise.all([
        refreshLists(),
        queryClient.invalidateQueries({ predicate: isQueryOfModel(scenario.id) }),
      ]),
  });
  const { mutateAsync: duplicateModel } = useMutation({
    mutationFn: async ({ scenario, label }: { scenario: ScenarioSummary; label: string }) => {
      const { data } = await api.post<ScenarioDetail>(
        `${scopeBase(ownScope(scenario))}/cashflow-scenarios/${scenario.id}/duplicate`,
        { label },
      );
      return data;
    },
    onSuccess: refreshLists,
  });
  const { mutateAsync: deleteModel } = useMutation({
    mutationFn: async (scenario: ScenarioSummary) => {
      await api.delete(`${scopeBase(ownScope(scenario))}/cashflow-scenarios/${scenario.id}`);
    },
    onSuccess: (_, scenario) => {
      queryClient.removeQueries({ predicate: isQueryOfModel(scenario.id) });
      return refreshLists();
    },
  });
  return { updateModel, duplicateModel, deleteModel };
}
